import {
  newRelicRUMAccountId,
  newRelicRUMAgentId,
  newRelicRUMApplicationId,
  newRelicRUMBeacon,
  newRelicRUMLicenseKey,
  newRelicRUMTrustKey,
} from '@gonfalon/constants/constants';
import { isNewRelicRumEnabled, newRelicErrorGroups } from '@gonfalon/dogfood-flags/dogfood-flags';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

import { getSessionUrl } from './getSessionUrl';

/**
 * New Relic Browser (RUM) agent.
 *
 * This runs in parallel with Datadog RUM and LaunchDarkly Observability behind
 * the `enable-web-app-new-relic-rum` flag. The telemetry wrappers in this
 * package dual-write to New Relic via the thin, no-op-safe helpers below, so
 * nothing happens until the agent has been initialized.
 */
let agent: BrowserAgent | undefined;

export function initializeNewRelicRum() {
  if (agent || !isNewRelicRumEnabled()) {
    return;
  }

  const licenseKey = newRelicRUMLicenseKey();
  const applicationID = newRelicRUMApplicationId();
  const beacon = newRelicRUMBeacon() || 'bam.nr-data.net';

  // Without an app id + license key there is nothing to report to.
  if (!licenseKey || !applicationID) {
    return;
  }

  agent = new BrowserAgent({
    // `info` is strictly typed as strings; the loader passes them through.
    info: {
      applicationID,
      licenseKey,
      beacon,
      errorBeacon: beacon,
      sa: 1,
    },
    // `loader_config` is loosely typed and expects numeric ids (matches the
    // copy/paste snippet from the New Relic UI).
    loader_config: {
      accountID: Number(newRelicRUMAccountId()),
      agentID: Number(newRelicRUMAgentId()),
      applicationID: Number(applicationID),
      licenseKey,
      trustKey: Number(newRelicRUMTrustKey()),
    },
    init: {
      // Don't instrument calls to New Relic's own beacon.
      ajax: { deny_list: [beacon] },
      distributed_tracing: { enabled: true },
      performance: { capture_detail: false, capture_marks: false, capture_measures: true },
      privacy: { cookies_enabled: true },
    },
  });

  agent.setErrorHandler(groupNoisyError);
}

/**
 * Fingerprints known-noisy errors into stable groups so they can be bulk-ignored in Errors Inbox.
 *
 * Without grouping, errors like "Loading chunk N failed" create dozens of separate groups (one per
 * chunk id/hash). Returning `{ group }` collapses them into one. Return `false` on no match — any
 * other truthy value drops the error entirely, which we avoid so data stays available during incidents.
 *
 * Patterns are evaluated per error (not once at init) so flag changes apply without a reload.
 */
function groupNoisyError(error: Error | string): false | { group: string } {
  const message = typeof error === 'string' ? error : error?.message;
  if (!message) {
    return false;
  }

  const match = newRelicErrorGroups().find((rule) => message.includes(rule.match));

  return match ? { group: match.group } : false;
}

export function getNewRelic(): BrowserAgent | undefined {
  return agent;
}

type NewRelicAttributes = Record<string, string | number | boolean>;

let sessionUrlPublished = false;

/**
 * Publish the session replay URL as a session-wide attribute, once, so it lands
 * on every event type rather than errors alone.
 *
 * Datadog gets this for free because its `beforeSend` hook runs per event. New
 * Relic has no equivalent — `setErrorHandler` can only ignore or group errors,
 * not decorate them — so `setCustomAttribute` is the only mechanism available.
 *
 * The replay session doesn't exist yet when the agent initializes and the SDK
 * exposes no session-start callback, so resolve it lazily here instead of
 * polling: the check rides along on telemetry we already emit and short-circuits
 * once it lands. Uses the stable session URL, which is safe to set once because
 * it points at the session rather than a moment in it; `trackError` still
 * overrides it per error with a timestamped link.
 *
 * `persist` is deliberately left off. Persisting would key the value to New
 * Relic's session lifetime, which can outlive the replay session and leave a
 * stale link on a fresh one.
 */
function publishSessionUrl() {
  if (sessionUrlPublished || !agent) {
    return;
  }

  const sessionURL = getSessionUrl();
  if (!sessionURL) {
    return;
  }

  agent.setCustomAttribute('sessionURL', sessionURL);
  sessionUrlPublished = true;
}

/**
 * Record a handled error. Mirrors `datadogRum.addError`.
 *
 * Callers pass a timestamped `sessionURL` per error, which takes precedence over
 * the session-wide attribute published here.
 */
export function nrNoticeError(error: Error, customAttributes?: NewRelicAttributes) {
  publishSessionUrl();
  agent?.noticeError(error, customAttributes);
}

/** Record a custom event/action. Mirrors `datadogRum.addAction`. */
export function nrAddPageAction(name: string, attributes?: NewRelicAttributes) {
  publishSessionUrl();
  agent?.addPageAction(name, attributes);
}

/** Set a global attribute on subsequent events. Mirrors `datadogRum.setGlobalContextProperty`. */
export function nrSetCustomAttribute(name: string, value: string | number | boolean | null) {
  agent?.setCustomAttribute(name, value);
}

/** Identify the current user. */
export function nrSetUserId(id: string | null) {
  agent?.setUserId(id);
}

/** Set the frontend build version. Mirrors the Datadog `version` init dimension. */
export function nrSetApplicationVersion(version: string | null) {
  agent?.setApplicationVersion(version);
}

/** Name the current SPA route so BrowserInteraction events are attributed correctly. */
export function nrSetCurrentRouteName(name: string) {
  agent?.setCurrentRouteName(name);
}
